// src/components/leitner/TaskTakerView.jsx
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  ArrowLeft, Play, RotateCcw, Trophy, BookOpen, BarChart3,
  Target, ChevronRight, Package, X, Lightbulb, Volume2, Send, CheckCircle2, Sparkles, FileSearch
} from 'lucide-react';
import { taskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import QuestionCard from './QuestionCard';
import NoteSectionCard from './NoteSectionCard';
import TaskPerformanceView from './TaskPerformanceView';
import { MathRenderer } from '../math/MathRenderer';
import QuestionMediaDisplay from './QuestionMediaDisplay';
import { learnerAnalyticsService } from '../../services/learnerAnalyticsService';
import InlineMathField from '../math/InlineMathField';
import { RESPONSE_TYPES } from './ResponseBase';
import CoLearnerPanel from './CoLearnerPanel';
import { useClaims } from '../../hooks/useClaims';

const MAX_SESSION_ATTEMPTS = 3;

const TaskTakerView = ({
  task,
  questions,
  notes = [],
  bookId,
  bookData,
  progress,
  onComplete,
  onBack,
  styling,
  isGuestMode = false,
  onGuestProgressUpdate
}) => {
  const { user } = useAuth();
  const [mode, setMode] = useState('menu');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [studyQueue, setStudyQueue] = useState([]);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0 });
  const [showPerformance, setShowPerformance] = useState(false);
  const [coLearnerOpen, setCoLearnerOpen] = useState(false);
  const [guidedIndex, setGuidedIndex] = useState(0);
  const [guidedAnswers, setGuidedAnswers] = useState({});
  const [guidedReveal, setGuidedReveal] = useState({});

  // Hint sidebar state
  const [hintSidebarOpen, setHintSidebarOpen] = useState(false);
  const [hintQuestion, setHintQuestion] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // All-at-once mode state
  const [allAnswers, setAllAnswers] = useState({});
  const [submittedQuestions, setSubmittedQuestions] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Track attempts per question in current session
  const [sessionAttempts, setSessionAttempts] = useState({});
  const { permissions, bookRole, loading } = useClaims(bookId);
  // Triggered notes state (shown after incorrect answer)
  const [triggeredNotes, setTriggeredNotes] = useState(null); // { notes: [], currentIndex: 0, questionId }

  // Section-based study queue
  const [studySections, setStudySections] = useState([]);

  // Branching state
  const [branchQueue, setBranchQueue] = useState([]);
  const [parentQuestionContext, setParentQuestionContext] = useState(null);
  const [branchAlert, setBranchAlert] = useState(null);

  const sessionIdRef = useRef(null);
  const sessionAnswersRef = useRef([]);

  const customDomain = bookData?.customDomain || bookData?.customPath;
  const isGuidedTask = task?.type === 'guided_reconstruction';
  const guidedData = task?.guidedReconstruction || {};
  const guidedSteps = Array.isArray(guidedData.steps) ? guidedData.steps : [];
  const displayMode = task?.displayMode || 'one-at-a-time';
  const displayModeLabel = displayMode === 'all-at-once'
    ? 'All questions at once'
    : displayMode === 'presentation'
      ? 'Presentation view'
      : 'One question at a time';
  const [isPresentationMode, setIsPresentationMode] = useState(displayMode === 'presentation');
  const showPresentationToggle = displayMode !== 'all-at-once';

  useEffect(() => {
    setIsPresentationMode(displayMode === 'presentation');
  }, [displayMode]);
  const coLearnerEnabled = task?.coLearnerEnabled !== false;

  // Flatten questions
  const flattenedQuestions = useMemo(() => {
    const flat = [];
    const publishedQuestions = questions.filter(q => q.status === 'published');

    publishedQuestions.forEach(q => {
      if (q.type === 'simple' || !q.type) {
        flat.push({
          ...q,
          isStem: false,
          text: q.text || q.questionText
        });
      } else if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach((subQ, idx) => {
          flat.push({
            ...subQ,
            id: subQ.id || `${q.id}_sub_${idx}`,
            isStem: true,
            stemId: q.id,
            stemText: q.stemText,
            stemImage: q.stemImage,
            stemMedia: q.media,
            parentQuestionId: q.id,
            text: subQ.text
          });
        });
      }
    });
    return flat;
  }, [questions]);

  const getQuestionBox = useCallback((questionId, isStem = false) => {
    if (isStem) {
      return progress?.subquestions?.[questionId]?.box || 1;
    }
    return progress?.questions?.[questionId]?.box || 1;
  }, [progress]);

  const boxDistribution = useMemo(() => {
    const distribution = { 1: [], 2: [], 3: [], 4: [] };
    flattenedQuestions.forEach(q => {
      const box = Math.min(getQuestionBox(q.id, q.isStem), 4);
      distribution[box].push(q);
    });
    return distribution;
  }, [flattenedQuestions, getQuestionBox]);

  const stats = useMemo(() => {
    const total = flattenedQuestions.length;
    const mastered = boxDistribution[4].length;
    const learning = boxDistribution[2].length + boxDistribution[3].length;
    const newQ = boxDistribution[1].length;
    const masteryPercent = total > 0 ? Math.round((mastered / total) * 100) : 0;
    return { total, mastered, learning, new: newQ, masteryPercent };
  }, [flattenedQuestions, boxDistribution]);

  // Determine if task uses sections
  const useSections = task?.sections?.length > 0;

  const noteLookup = useMemo(() => {
    const map = new Map();
    (notes || []).forEach((note) => {
      const ids = [note?.id, note?.noteId, note?.docId]
        .filter((value) => value !== undefined && value !== null)
        .map(String);
      ids.forEach((id) => map.set(id, note));
    });
    return map;
  }, [notes]);

  const resolveNoteById = useCallback((noteId) => {
    if (noteId === undefined || noteId === null) return null;
    return noteLookup.get(String(noteId)) || null;
  }, [noteLookup]);

  const resolveNoteFromSection = useCallback((section) => {
    if (!section) return null;
    if (section.note && typeof section.note === 'object') return section.note;

    const sectionNoteId = section.id ?? section.noteId ?? section.noteRefId ?? null;
    return resolveNoteById(sectionNoteId);
  }, [resolveNoteById]);

  const buildStudyQueue = useCallback(() => {
    let queue = [];
    if (task.shuffleQuestions !== false) {
      [1, 2, 3, 4].forEach(box => {
        const shuffled = [...boxDistribution[box]].sort(() => Math.random() - 0.5);
        queue.push(...shuffled);
      });
    } else {
      queue = [...flattenedQuestions];
    }
    return queue.slice(0, task.maxQuestionsPerSession || flattenedQuestions.length);
  }, [boxDistribution, task.maxQuestionsPerSession, task.shuffleQuestions, flattenedQuestions]);

  // Build section-based study queue
  const buildSectionQueue = useCallback(() => {
    if (!task?.sections?.length) return [];
    return task.sections.flatMap(section => {
      if (section.type === 'note') {
        const note = resolveNoteFromSection(section);
        return [{ sectionType: 'note', note, minReadingTime: section.minReadingTime || 30, sectionData: section }];
      }

      // Question section: support simple questions, direct sub-question IDs, and stem parent IDs.
      const sectionQuestionId = section.id ?? section.questionId;
      const directFlatQuestion = flattenedQuestions.find(q => String(q.id) === String(sectionQuestionId));
      if (directFlatQuestion) {
        return [{ sectionType: 'question', question: directFlatQuestion, sectionData: section }];
      }

      const sourceQuestion = questions.find(q => String(q.id) === String(sectionQuestionId));
      if (sourceQuestion?.type === 'stem' && Array.isArray(sourceQuestion.subQuestions) && sourceQuestion.subQuestions.length > 0) {
        return sourceQuestion.subQuestions.map((subQ, idx) => ({
          sectionType: 'question',
          question: {
            ...subQ,
            id: subQ.id || `${sourceQuestion.id}_sub_${idx}`,
            isStem: true,
            stemId: sourceQuestion.id,
            stemText: sourceQuestion.stemText,
            stemImage: sourceQuestion.stemImage,
            stemMedia: sourceQuestion.media,
            parentQuestionId: sourceQuestion.id,
            text: subQ.text
          },
          sectionData: section
        }));
      }

      if (sourceQuestion) {
        return [{
          sectionType: 'question',
          question: {
            ...sourceQuestion,
            isStem: false,
            text: sourceQuestion.text || sourceQuestion.questionText
          },
          sectionData: section
        }];
      }

      // Backward-compat fallback: section references a sub-question ID while only stem parent is loaded.
      for (const parent of questions) {
        if (parent?.type !== 'stem' || !Array.isArray(parent.subQuestions)) continue;
        const subIdx = parent.subQuestions.findIndex((sq) => String(sq.id) === String(sectionQuestionId));
        if (subIdx !== -1) {
          const subQ = parent.subQuestions[subIdx];
          return [{
            sectionType: 'question',
            question: {
              ...subQ,
              id: subQ.id || `${parent.id}_sub_${subIdx}`,
              isStem: true,
              stemId: parent.id,
              stemText: parent.stemText,
              stemImage: parent.stemImage,
              stemMedia: parent.media,
              parentQuestionId: parent.id,
              text: subQ.text
            },
            sectionData: section
          }];
        }
      }

      return [];
    }).filter(entry =>
      (entry.sectionType === 'note' && entry.note) ||
      (entry.sectionType === 'question' && entry.question)
    );
  }, [task?.sections, flattenedQuestions, questions, resolveNoteFromSection]);

  // Group questions by stem for all-at-once mode
  const groupedQuestions = useMemo(() => {
    if (!studyQueue.length) return [];

    const groups = [];
    let currentStemId = null;
    let currentGroup = null;

    studyQueue.forEach((q, idx) => {
      if (q.isStem) {
        if (q.stemId !== currentStemId) {
          if (currentGroup) groups.push(currentGroup);
          currentStemId = q.stemId;
          currentGroup = {
            stemId: q.stemId,
            stemText: q.stemText,
            stemImage: q.stemImage,
            stemMedia: q.stemMedia,
            questions: [{ ...q, displayIndex: idx }]
          };
        } else {
          currentGroup.questions.push({ ...q, displayIndex: idx });
        }
      } else {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = null;
        currentStemId = null;
        groups.push({ stemId: null, questions: [{ ...q, displayIndex: idx }] });
      }
    });
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [studyQueue]);

  // Count answered questions for all-at-once mode
  const answeredCount = useMemo(() => {
    return studyQueue.filter(q => {
      const hasOptionAnswer = (allAnswers[q.id] || []).length > 0;
      const hasTextAnswer = (allAnswers[`${q.id}_text`] || '').trim().length > 0;
      return hasOptionAnswer || hasTextAnswer;
    }).length;
  }, [studyQueue, allAnswers]);

  const startStudy = () => {
    sessionAnswersRef.current = [];

    if (useSections) {
      const sectionQueue = buildSectionQueue();
      if (sectionQueue.length === 0) {
        alert('No content to study!');
        return;
      }
      setStudySections(sectionQueue);
      // Also build a question-only queue for backward compat stats
      const questionOnly = sectionQueue
        .filter(s => s.sectionType === 'question')
        .map(s => s.question);
      setStudyQueue(questionOnly);
    } else {
      const queue = buildStudyQueue();
      if (queue.length === 0) {
        alert('No questions to study!');
        return;
      }
      setStudyQueue(queue);
      setStudySections([]);
    }

    setCurrentIndex(0);
    setSessionStats({ correct: 0, incorrect: 0 });
    setSessionAttempts({});
    setHintSidebarOpen(false);
    setHintQuestion(null);
    setIsRetrying(false);
    setAllAnswers({});
    setSubmittedQuestions({});
    setTriggeredNotes(null);
    setMode('study');
    setBranchQueue([]);
    setParentQuestionContext(null);
    setBranchAlert(null);
  };

  const startGuidedSession = () => {
    setGuidedIndex(0);
    setGuidedAnswers({});
    setGuidedReveal({});
    setSessionStats({ correct: 0, incorrect: 0 });
    setMode('guided');
  };

  const handleRequestHint = (question) => {
    setHintQuestion(question);
    setHintSidebarOpen(true);
  };

  const renderCoLearnerButton = (className = '', isFloating = false) => {
    if (!coLearnerEnabled) return null;
    if (isFloating) {
      return (
        <button
          onClick={() => setCoLearnerOpen(true)}
          className={`fixed bottom-6 right-6 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-2xl flex items-center justify-center hover:bg-indigo-700 hover:scale-110 active:scale-95 transition-all z-40 group ${coLearnerOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'} ${className}`}
          title="Open Co-Learner Agent"
        >
          <Sparkles className="h-6 w-6" />
          <div className="absolute top-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-900 rounded-full" />
          <span className="absolute right-full mr-3 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-700 pointer-events-none font-medium">
            Ask Co-Learner Agent
          </span>
        </button>
      );
    }

    return (
      <button
        onClick={() => setCoLearnerOpen(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all transform hover:scale-105 active:scale-95 ${className}`}
      >
        <Sparkles className="h-4 w-4" />
        Co-Learner Agent
      </button>
    );
  };

  const handleRetryWithHint = () => {
    const currentQ = studyQueue[currentIndex];
    setSessionAttempts(prev => ({
      ...prev,
      [currentQ.id]: (prev[currentQ.id] || 0) + 1
    }));
    setIsRetrying(true);
    closeHintSidebar();
  };

  const closeHintSidebar = () => {
    setHintSidebarOpen(false);
    setHintQuestion(null);
  };

  // One-at-a-time answer handler
  const handleAnswerSubmit = async (isCorrect, selectedAnswer) => {
    const sectionQuestion = useSections ? studySections[currentIndex]?.question : null;
    const currentQuestion = sectionQuestion || studyQueue[currentIndex];
    if (!currentQuestion) {
      console.warn('No current question found while submitting answer', { currentIndex, useSections });
      advanceToNext();
      return;
    }
    const questionId = currentQuestion.id;

    // Check for branch trigger
    if (currentQuestion.responseType === RESPONSE_TYPES.MULTISELECT && selectedAnswer && Array.isArray(selectedAnswer)) {
      const selectedOptions = (currentQuestion.options || [])
        .filter(opt => selectedAnswer.includes(opt.id));

      const branchIds = selectedOptions.flatMap(opt => opt.branchQuestionIds || []);

      if (branchIds.length > 0) {
        // We found branches!
        const branchQuestions = branchIds
          .map(id => questions.find(q => q.id === id))
          .filter(Boolean);

        if (branchQuestions.length > 0) {
          setBranchQueue(branchQuestions);
          setParentQuestionContext(currentQuestion);
          setBranchAlert("Branching to supplemental questions. Answer these to return to the main question.");
          // We don't submit the main question yet, or maybe we do? 
          // The request says "After answering such branch question, the flow return to the main question for the learner to attempt again."
          // So we should NOT submit the main question progress yet if it has branches?
          // Actually, requirement 4 says: "learner is directed to branch question whether the question is answered correctly or incorrect"
          // And requirement 3 says: "The learner must correctly answer all branch question to return to the main question."
          // I will submit the main question progress ONLY if there are no branches, OR wait until they return?
          // If they return to "attempt again", it implies the first attempt was recorded or at least didn't end the question.
          return; // Stop here and show branches
        }
      }
    }

    // Branch question logic
    if (parentQuestionContext && branchQueue.length > 0) {
      if (!isCorrect) {
        // Requirement 3: must answer correctly to return/proceed
        return;
      }

      const newQueue = [...branchQueue];
      newQueue.shift();
      setBranchQueue(newQueue);

      if (newQueue.length > 0) {
        // Show next branch
        return;
      } else {
        // Finished all branches, return to main
        setBranchQueue([]);
        // Requirement: "return to the main question for the learner to attempt again"
        // This means we stay on the same currentIndex but need to reset its internal submitted state.
        // I'll need to pass a 'reset' signal to QuestionCard.
        setBranchAlert("Supplemental questions complete. Returning to main question.");
        setTimeout(() => {
          setParentQuestionContext(null);
          setBranchAlert(null);
        }, 2000);
        return;
      }
    }

    const boxBefore = getQuestionBox(questionId, currentQuestion.isStem);

    let boxAfter = boxBefore;
    if (!isGuestMode && user) {
      const result = await taskService.updateTaskQuestionProgress(
        bookId, task.id, user.uid, questionId, isCorrect
      );
      boxAfter = result?.box || (isCorrect ? Math.min(boxBefore + 1, 5) : 1);
    }

    sessionAnswersRef.current.push({
      questionId, correct: isCorrect, timeSpent: 0, boxBefore, boxAfter
    });

    setSessionStats(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      incorrect: prev.incorrect + (isCorrect ? 0 : 1)
    }));

    // Check for incorrect-answer note trigger (sections mode)
    if (!isCorrect && useSections && studySections[currentIndex]) {
      const sectionData = studySections[currentIndex].sectionData;
      const incorrectNoteIds = sectionData?.incorrectNoteIds || [];
      if (incorrectNoteIds.length > 0) {
        const triggeredNoteObjects = incorrectNoteIds
          .map((nid) => resolveNoteById(nid))
          .filter(Boolean);
        if (triggeredNoteObjects.length > 0) {
          setTriggeredNotes({
            notes: triggeredNoteObjects,
            currentIndex: 0,
            questionId: questionId,
            minReadingTimes: incorrectNoteIds.map(nid => {
              const sec = (task.sections || []).find(s =>
                s.type === 'note' && String(s.id ?? s.noteId) === String(nid)
              );
              return sec?.minReadingTime || 30;
            })
          });
          return; // Don't advance yet - show triggered notes first
        }
      }
    }

    advanceToNext();
  };

  // Advance to next section/question
  const advanceToNext = () => {
    const maxIndex = useSections ? studySections.length - 1 : studyQueue.length - 1;
    if (currentIndex < maxIndex) {
      setCurrentIndex(prev => prev + 1);
    } else {
      completeStudySession();
      setMode('complete');
    }
  };

  // Handle note section completion (inline notes in section queue)
  const handleNoteSectionComplete = async (rating, timeSpent) => {
    if (!useSections) return;
    const currentSection = studySections[currentIndex];
    if (!currentSection || currentSection.sectionType !== 'note') return;

    // Save note reading
    if (!isGuestMode && user) {
      try {
        await taskService.updateNoteReading(bookId, task.id, user.uid, currentSection.note.id, {
          rating,
          timeSpent,
          triggeredBy: null
        });
      } catch (err) {
        console.error('Failed to save note reading:', err);
      }
    }

    advanceToNext();
  };

  // Handle triggered note completion (shown after incorrect answer)
  const handleTriggeredNoteComplete = async (rating, timeSpent) => {
    if (!triggeredNotes) return;

    const currentNote = triggeredNotes.notes[triggeredNotes.currentIndex];

    // Save note reading
    if (!isGuestMode && user && currentNote) {
      try {
        await taskService.updateNoteReading(bookId, task.id, user.uid, currentNote.id, {
          rating,
          timeSpent,
          triggeredBy: triggeredNotes.questionId
        });
      } catch (err) {
        console.error('Failed to save triggered note reading:', err);
      }
    }

    // Move to next triggered note or finish
    if (triggeredNotes.currentIndex < triggeredNotes.notes.length - 1) {
      setTriggeredNotes(prev => ({ ...prev, currentIndex: prev.currentIndex + 1 }));
    } else {
      // All triggered notes read, advance to next section
      setTriggeredNotes(null);
      advanceToNext();
    }
  };

  // All-at-once answer selection (doesn't submit yet)
  const handleAllAtOnceSelect = (questionId, selectedOptionIds) => {
    setAllAnswers(prev => ({ ...prev, [questionId]: selectedOptionIds }));
  };

  // Submit all answers at once
  const handleSubmitAll = async () => {
    setSubmitting(true);
    let correct = 0;
    let incorrect = 0;

    for (const question of studyQueue) {
      const selectedIds = allAnswers[question.id] || [];
      const correctIds = question.correctOptionIds || [];
      const isCorrect = selectedIds.length === correctIds.length &&
        selectedIds.every(id => correctIds.includes(id));

      const boxBefore = getQuestionBox(question.id, question.isStem);
      let boxAfter = boxBefore;

      if (!isGuestMode && user) {
        const result = await taskService.updateTaskQuestionProgress(
          bookId, task.id, user.uid, question.id, isCorrect
        );
        boxAfter = result?.box || (isCorrect ? Math.min(boxBefore + 1, 5) : 1);
      }

      sessionAnswersRef.current.push({
        questionId: question.id, correct: isCorrect, timeSpent: 0, boxBefore, boxAfter
      });

      setSubmittedQuestions(prev => ({
        ...prev,
        [question.id]: { isCorrect, selectedIds, correctIds }
      }));

      if (isCorrect) correct++;
      else incorrect++;
    }

    setSessionStats({ correct, incorrect });
    await completeStudySession();
    setSubmitting(false);
    setMode('complete');
  };

  const completeStudySession = async () => {
    if (isGuestMode || !user) return;
    try {
      await learnerAnalyticsService.recordQuickSession(bookId, task.id, user.uid, {
        answers: sessionAnswersRef.current,
        duration: sessionAnswersRef.current.reduce((sum, a) => sum + (a.timeSpent || 0), 0)
      });
    } catch (error) {
      console.error('Failed to record session:', error);
    }
  };

  const currentQuestion = studyQueue[currentIndex];

  // ==================== PERFORMANCE VIEW ====================
  if (showPerformance) {
    return (
      <>
        <TaskPerformanceView
          bookId={bookId}
          task={task}
          questions={questions}
          styling={styling}
          viewMode="learner"
          onBack={() => setShowPerformance(false)}
        />
        {coLearnerEnabled && permissions.canEdit && (
          <CoLearnerPanel
            isOpen={coLearnerOpen}
            onClose={() => setCoLearnerOpen(false)}
            bookId={bookId}
            task={task}
            questions={questions}
            progress={progress}
            styling={styling}
          />
        )}
      </>
    );
  }

  // ==================== MENU VIEW ====================
  if (mode === 'menu') {
    return (
      <div className="space-y-6 pb-24 sm:pb-8">
        <div className="flex items-center justify-between">
          <button onClick={'https://parrotbooks.co.za/leitner/tasks'} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          {renderCoLearnerButton()}
        </div>

        <div className="text-center py-8">
          {isGuidedTask ? (
            <FileSearch className="h-12 w-12 text-indigo-400 mx-auto mb-3" />
          ) : (
            <BookOpen className="h-12 w-12 text-indigo-400 mx-auto mb-3" />
          )}
          <h2 className="text-2xl font-bold text-white mb-2">{task?.name}</h2>
          {task?.description && <p className="text-slate-400">{task.description}</p>}
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-slate-800 rounded-full text-sm text-slate-300">
            {displayModeLabel}
          </div>
        </div>

        <div className={`grid gap-3 ${isGuidedTask ? 'grid-cols-3' : (useSections ? 'grid-cols-5' : 'grid-cols-4')}`}>
          {[
            ...(isGuidedTask ? [
              { label: 'Steps', value: guidedSteps.length, color: 'text-white' },
              { label: 'Completed', value: Object.keys(guidedReveal).length, color: 'text-green-400' },
              { label: 'Remaining', value: Math.max(guidedSteps.length - Object.keys(guidedReveal).length, 0), color: 'text-amber-400' }
            ] : [
              { label: 'Total', value: stats.total, color: 'text-white' },
              { label: 'New', value: stats.new, color: 'text-blue-400' },
              { label: 'Learning', value: stats.learning, color: 'text-amber-400' },
              { label: 'Mastered', value: stats.mastered, color: 'text-green-400' },
              ...(useSections ? [{ label: 'Notes', value: (task.sections || []).filter(s => s.type === 'note').length, color: 'text-teal-400' }] : [])
            ])
          ].map(s => (
            <div key={s.label} className="bg-slate-800 rounded-lg p-4 text-center border border-slate-700">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-sm text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">{isGuidedTask ? 'Guided Progress' : 'Overall Mastery'}</span>
            <span className="text-white font-medium">
              {isGuidedTask
                ? `${guidedSteps.length > 0 ? Math.round((Object.keys(guidedReveal).length / guidedSteps.length) * 100) : 0}%`
                : `${stats.masteryPercent}%`}
            </span>
          </div>
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-green-500 transition-all"
              style={{
                width: isGuidedTask
                  ? `${guidedSteps.length > 0 ? Math.round((Object.keys(guidedReveal).length / guidedSteps.length) * 100) : 0}%`
                  : `${stats.masteryPercent}%`
              }}
            />
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={isGuidedTask ? startGuidedSession : startStudy}
            className="px-8 py-4 rounded-lg text-white font-semibold transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
          >
            <Play className="h-6 w-6" />
            {isGuidedTask ? 'Start Guided Session' : `Start ${displayMode === 'all-at-once' ? 'Test' : 'Study Session'}`}
          </button>
          <button onClick={() => setShowPerformance(true)} className="px-6 py-4 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            My Progress
          </button>
        </div>

        {coLearnerEnabled && (
          <CoLearnerPanel
            isOpen={coLearnerOpen}
            onClose={() => setCoLearnerOpen(false)}
            bookId={bookId}
            task={task}
            questions={questions}
            progress={progress}
            styling={styling}
          />
        )}
      </div>
    );
  }

  // ==================== GUIDED RECONSTRUCTION VIEW ====================
  if (mode === 'guided' && isGuidedTask) {
    const totalSteps = guidedSteps.length;
    const activeStep = guidedSteps[guidedIndex];
    const isLastStep = guidedIndex >= totalSteps - 1;
    const answerKey = activeStep?.id || `step_${guidedIndex}`;
    const answerValue = guidedAnswers[answerKey] || '';
    const isRevealed = !!guidedReveal[answerKey];

    if (!activeStep) {
      return (
        <div className="space-y-6 pb-24 sm:pb-8 text-center">
          <p className="text-slate-300">No guided steps found for this task.</p>
          <button
            onClick={() => setMode('menu')}
            className="px-5 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white"
          >
            Back
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6 pb-24 sm:pb-8">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMode('menu')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Exit Session
          </button>
          <span className="text-slate-400">Step {guidedIndex + 1} / {totalSteps}</span>
        </div>

        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${((guidedIndex + 1) / Math.max(totalSteps, 1)) * 100}%`,
              backgroundColor: styling?.primaryColor || '#6366f1'
            }}
          />
        </div>

        <div className="bg-slate-800 rounded-lg border border-slate-700 p-5 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">Step {guidedIndex + 1}</div>
            <h3 className="text-xl font-semibold text-white">{activeStep.title}</h3>
            {activeStep.objective && <p className="text-sm text-slate-400 mt-1">{activeStep.objective}</p>}
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase mb-2">Worked Example</div>
            <div className="prose prose-sm prose-invert max-w-none text-slate-200">
              <MathRenderer content={activeStep.workedExample || ''} enableMarkdown={true} enableMath={true} />
            </div>
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-4">
            <div className="text-xs font-semibold text-slate-400 uppercase mb-2">Practice Question</div>
            <div className="prose prose-sm prose-invert max-w-none text-slate-100">
              <MathRenderer content={activeStep.practiceQuestion || ''} enableMarkdown={true} enableMath={true} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Response</label>
            <InlineMathField
              value={answerValue}
              onChange={(value) => setGuidedAnswers(prev => ({ ...prev, [answerKey]: value }))}
              rows={4}
              placeholder="Type your attempt here... (Ctrl+M for math)"
            />
          </div>

          {!isRevealed && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setGuidedReveal(prev => ({ ...prev, [answerKey]: true }))}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium"
              >
                Reveal Model Answer
              </button>
              {activeStep.hints?.length > 0 && (
                <span className="text-xs text-slate-400">{activeStep.hints.length} hint{activeStep.hints.length === 1 ? '' : 's'} available</span>
              )}
            </div>
          )}

          {isRevealed && (
            <div className="space-y-3">
              {activeStep.hints?.length > 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                  <div className="text-xs font-semibold text-amber-300 uppercase mb-1">Hints</div>
                  <ul className="list-disc list-inside text-sm text-amber-100 space-y-1">
                    {activeStep.hints.map((hint, idx) => (
                      <li key={`${answerKey}_hint_${idx}`}>{hint}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3">
                <div className="text-xs font-semibold text-emerald-300 uppercase mb-1">Model Answer</div>
                <div className="prose prose-sm prose-invert max-w-none text-emerald-100">
                  <MathRenderer content={activeStep.modelAnswer || ''} enableMarkdown={true} enableMath={true} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setGuidedIndex((idx) => Math.max(idx - 1, 0))}
            disabled={guidedIndex === 0}
            className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => {
              if (!isLastStep) {
                setGuidedIndex((idx) => Math.min(idx + 1, totalSteps - 1));
                return;
              }
              setMode('guided-complete');
            }}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isLastStep ? 'Finish Task' : 'Next Step'}
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'guided-complete' && isGuidedTask) {
    const revealedCount = Object.keys(guidedReveal).length;
    return (
      <div className="text-center space-y-6 pb-24 sm:pb-8">
        <div className="py-8">
          <Trophy className="h-16 w-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Guided Session Complete</h2>
          <p className="text-slate-400">You worked through {guidedSteps.length} guided step{guidedSteps.length === 1 ? '' : 's'}.</p>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 max-w-md mx-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-3xl font-bold text-white">{guidedSteps.length}</div>
              <div className="text-sm text-slate-400">Total Steps</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-indigo-300">{revealedCount}</div>
              <div className="text-sm text-slate-400">Model Answers Viewed</div>
            </div>
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={() => {
              onComplete({
                correct: 0,
                incorrect: 0,
                questionsStudied: guidedSteps.length,
                guidedStepsCompleted: guidedSteps.length,
                guidedModelAnswersViewed: revealedCount,
                accuracy: 0,
                answers: guidedAnswers
              });
            }}
            className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors"
          >
            Done
          </button>
          <button
            onClick={startGuidedSession}
            className="px-6 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
          >
            <RotateCcw className="h-5 w-5" />
            Restart Guided Session
          </button>
        </div>
      </div>
    );
  }

  // ==================== ALL-AT-ONCE STUDY VIEW ====================
  if (mode === 'study' && displayMode === 'all-at-once') {

    return (
      <div className="space-y-6 pb-24 sm:pb-10">
        <div className="flex items-center justify-between sticky top-0 bg-slate-900/95 backdrop-blur py-3 -mx-2 px-2 z-10 border-b border-slate-700">
          <button
            onClick={() => {
              if (window.confirm('Exit? Your progress will not be saved.')) {
                setMode('menu');
              }
            }}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Exit
          </button>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">
              {answeredCount} / {studyQueue.length} answered
            </span>
            {renderCoLearnerButton('hidden sm:flex')}
            <button
              onClick={handleSubmitAll}
              disabled={submitting || answeredCount === 0}
              className="px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
            >
              {submitting ? <span className="animate-spin">⏳</span> : <Send className="h-4 w-4" />}
              Submit All
            </button>
          </div>
        </div>

        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{ width: `${(answeredCount / studyQueue.length) * 100}%`, backgroundColor: styling?.primaryColor || '#6366f1' }}
          />
        </div>

        <div className="space-y-8">
          {groupedQuestions.map((group, groupIdx) => (
            <div key={group.stemId || `group-${groupIdx}`} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
              {/* Stem Header - Only show once per group */}
              {group.stemId && group.stemText && (
                <div className="p-5 bg-indigo-500/10 border-b border-slate-700">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-indigo-500/20 text-indigo-300 text-xs font-medium rounded">
                      CONTEXT
                    </span>
                  </div>
                  <div className="text-slate-100 text-base leading-relaxed">
                    <MathRenderer content={group.stemText} enableMarkdown={true} enableMath={true} />
                  </div>

                  {/* Legacy stem image */}
                  {group.stemImage && (
                    <img src={group.stemImage} alt="Context" className="mt-4 max-h-64 rounded-lg border border-slate-600" />
                  )}

                  {/* New media format */}
                  {group.stemMedia && group.stemMedia.type && group.stemMedia.type !== 'none' && (
                    <div className="mt-4">
                      <QuestionMediaDisplay media={group.stemMedia} className="max-w-2xl mx-auto" />
                    </div>
                  )}
                </div>
              )}

              {/* Questions in this group */}
              <div className="divide-y divide-slate-700">
                {group.questions.map((question) => {
                  const selectedIds = allAnswers[question.id] || [];
                  const textAnswer = allAnswers[`${question.id}_text`] || '';
                  const isAnswered = selectedIds.length > 0 || textAnswer.trim().length > 0;
                  const hasOptions = question.options && question.options.length > 0;
                  const showImmediateAnswers = task?.showAnswersAfterEachResponse === true;
                  const submittedInfo = submittedQuestions[question.id];

                  return (
                    <div key={question.id} className="p-5">
                      <div className="flex flex-col gap-3 mb-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shrink-0 ${isAnswered ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-300'
                              }`}>
                              {question.displayIndex + 1}
                            </span>
                          </div>
                          {isAnswered && <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0 mt-1" />}
                        </div>
                        <div className="w-full">
                          <div className="text-slate-100 text-base">
                            <MathRenderer content={question.text || question.questionText} enableMarkdown={true} enableMath={true} />
                          </div>

                          {/* Legacy question image */}
                          {question.image && (
                            <img src={question.image} alt="Question" className="mt-3 max-h-48 rounded-lg border border-slate-600" />
                          )}

                          {/* New media format */}
                          {question.media && question.media.type && question.media.type !== 'none' && (
                            <div className="mt-3">
                              <QuestionMediaDisplay media={question.media} className="max-w-lg mx-auto" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* MCQ Options */}
                      {hasOptions ? (
                        <div className="space-y-2 w-full">
                          {question.options.map(option => {
                            const isSelected = selectedIds.includes(option.id);
                            return (
                              <button
                                key={option.id}
                                onClick={() => {
                                  const isMultiple = question.correctOptionIds?.length > 1;
                                  if (isMultiple) {
                                    handleAllAtOnceSelect(
                                      question.id,
                                      isSelected ? selectedIds.filter(id => id !== option.id) : [...selectedIds, option.id]
                                    );
                                  } else {
                                    handleAllAtOnceSelect(question.id, [option.id]);
                                  }
                                }}
                                className={`w-full p-3 rounded-lg text-left transition-all border ${isSelected
                                  ? 'border-indigo-400 bg-indigo-500/20 text-slate-100'
                                  : 'border-slate-600 bg-slate-700/30 text-slate-200 hover:border-slate-500 hover:bg-slate-700/50'
                                  }`}
                              >
                                <MathRenderer content={option.text} enableMarkdown={true} enableMath={true} />
                              </button>
                            );
                          })}
                          {showImmediateAnswers && submittedInfo && (
                            <div className="mt-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-emerald-200">Correct Answer{question.correctOptionIds?.length > 1 ? 's' : ''}</span>
                                <span className={`text-xs font-semibold ${submittedInfo.isCorrect ? 'text-emerald-300' : 'text-amber-300'}`}>
                                  {submittedInfo.isCorrect ? 'You were correct' : 'Review the correct answer'}
                                </span>
                              </div>
                              <div className="space-y-2">
                                {(question.options || [])
                                  .filter(opt => question.correctOptionIds?.includes(opt.id))
                                  .map(opt => (
                                    <div key={opt.id} className="rounded-md border border-emerald-500/30 bg-emerald-900/20 p-2">
                                      <div className="text-emerald-100">
                                        <MathRenderer content={opt.text} enableMarkdown={true} enableMath={true} />
                                      </div>
                                      {opt.reasoning && (
                                        <div className="mt-2 text-emerald-200/90 text-xs">
                                          <div className="font-semibold text-emerald-200 mb-1">Reasoning</div>
                                          <MathRenderer content={opt.reasoning} enableMarkdown={true} enableMath={true} />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Text Response Area for non-MCQ with Preview */
                        <div className="w-full">
                          {/* Edit/Preview Toggle */}
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              type="button"
                              onClick={() => setAllAnswers(prev => ({
                                ...prev,
                                [`${question.id}_preview`]: false
                              }))}
                              className={`px-3 py-1 text-xs rounded-md transition-all ${!allAnswers[`${question.id}_preview`]
                                ? 'bg-indigo-500 text-white'
                                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                                }`}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setAllAnswers(prev => ({
                                ...prev,
                                [`${question.id}_preview`]: true
                              }))}
                              disabled={!textAnswer.trim()}
                              className={`px-3 py-1 text-xs rounded-md transition-all ${allAnswers[`${question.id}_preview`]
                                ? 'bg-indigo-500 text-white'
                                : 'bg-slate-700 text-slate-400 hover:bg-slate-600 disabled:opacity-50'
                                }`}
                            >
                              Preview
                            </button>
                          </div>

                          {/* Edit Mode */}
                          {!allAnswers[`${question.id}_preview`] ? (
                            <div className="bg-slate-700/30 rounded-lg border border-slate-600 focus-within:border-indigo-400">
                              <InlineMathField
                                value={textAnswer}
                                onChange={(val) => setAllAnswers(prev => ({
                                  ...prev,
                                  [`${question.id}_text`]: val
                                }))}
                                placeholder="Type your answer here... (Ctrl+M for math)"
                                rows={4}
                                normalizeOnPaste={true}
                              />
                            </div>
                          ) : (
                            /* Preview Mode */
                            <div className="min-h-[120px] p-4 rounded-lg border border-indigo-500/50 bg-slate-800/50">
                              {textAnswer.trim() ? (
                                <div className="text-slate-100 prose prose-invert prose-sm max-w-none">
                                  <MathRenderer content={textAnswer} enableMarkdown={true} enableMath={true} />
                                </div>
                              ) : (
                                <p className="text-slate-500 italic">No content to preview</p>
                              )}
                            </div>
                          )}

                          <p className="text-xs text-slate-500 mt-2 flex items-center justify-between">
                            <span>
                              {textAnswer.length > 0
                                ? `${textAnswer.split(/\s+/).filter(Boolean).length} words`
                                : 'Enter your response (Ctrl+M for math)'}
                            </span>
                            {textAnswer.includes('$') && (
                              <span className="text-indigo-400">Contains math expressions</span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          ))}
        </div>

        {renderCoLearnerButton('', true)}

        <div className="sticky bottom-4 flex justify-center">
          <button
            onClick={handleSubmitAll}
            disabled={submitting || answeredCount === 0}
            className="px-8 py-4 rounded-xl text-white font-semibold transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50 shadow-2xl"
            style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
          >
            {submitting ? 'Submitting...' : `Submit All (${answeredCount}/${studyQueue.length})`}
          </button>
        </div>

        {coLearnerEnabled && (
          <CoLearnerPanel
            isOpen={coLearnerOpen}
            onClose={() => setCoLearnerOpen(false)}
            bookId={bookId}
            task={task}
            questions={questions}
            progress={progress}
            styling={styling}
          />
        )}
      </div>
    );
  }

  // ==================== SECTION-BASED STUDY VIEW ====================
  if (mode === 'study' && useSections && studySections.length > 0 && displayMode !== 'all-at-once') {
    const totalSections = studySections.length;
    const currentSection = studySections[currentIndex];

    // If showing triggered notes
    if (triggeredNotes) {
      const trigNote = triggeredNotes.notes[triggeredNotes.currentIndex];
      const trigMinTime = triggeredNotes.minReadingTimes?.[triggeredNotes.currentIndex] || 30;
      return (
        <div className="flex flex-col gap-4 max-w-4xl mx-auto pb-24 sm:pb-8">
          <div className="flex items-center justify-between">
            <span className="text-amber-400 text-sm font-medium">
              Review Material ({triggeredNotes.currentIndex + 1} of {triggeredNotes.notes.length})
            </span>
            <span className="text-slate-400 text-sm">
              {currentIndex + 1} / {totalSections}
            </span>
          </div>
          <NoteSectionCard
            key={`triggered-${trigNote.id}`}
            note={trigNote}
            minReadingTime={trigMinTime}
            onComplete={handleTriggeredNoteComplete}
            isTriggered={true}
            styling={styling}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4 max-w-4xl mx-auto pb-24 sm:pb-8">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              if (window.confirm('Exit study session? Progress is saved.')) {
                setMode('menu');
              }
            }}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Exit Session
          </button>
          <span className="text-slate-400">
            {currentIndex + 1} / {totalSections}
          </span>
        </div>

        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${((currentIndex + 1) / totalSections) * 100}%`,
              backgroundColor: styling?.primaryColor || '#6366f1'
            }}
          />
        </div>

        {currentSection?.sectionType === 'note' ? (
          <NoteSectionCard
            key={`note-${currentSection.note.id}-${currentIndex}`}
            note={currentSection.note}
            minReadingTime={currentSection.minReadingTime}
            onComplete={handleNoteSectionComplete}
            isTriggered={false}
            styling={styling}
          />
        ) : currentSection?.sectionType === 'question' && currentSection.question ? (
          <>
            {!parentQuestionContext && (
              <div className="flex items-center gap-2 justify-center">
                <span className="text-sm text-slate-400">Current Box:</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map(box => {
                    const qBox = getQuestionBox(currentSection.question.id, currentSection.question.isStem);
                    return (
                      <div
                        key={box}
                        className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold transition-all ${qBox === box ? 'text-white scale-110' : qBox > box ? 'bg-green-500/30 text-green-400' : 'bg-slate-700 text-slate-500'}`}
                        style={qBox === box ? { backgroundColor: styling?.primaryColor || '#6366f1' } : {}}
                      >
                        {box}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <QuestionCard
              key={`${currentSection.question.id}-${currentIndex}`}
              question={{ ...currentSection.question, text: currentSection.question.text || currentSection.question.questionText }}
              image={currentSection.question.image}
              onSubmit={handleAnswerSubmit}
              layout={isPresentationMode ? 'split' : 'stacked'}
              stemText={currentSection.question.isStem ? currentSection.question.stemText : undefined}
              stemImage={currentSection.question.isStem ? currentSection.question.stemImage : undefined}
              stemMedia={currentSection.question.isStem ? currentSection.question.stemMedia : undefined}
              showAnswersAfterEachResponse={task?.showAnswersAfterEachResponse === true}
            />
          </>
        ) : null}

        <div className="flex justify-center gap-6 text-sm">
          <span className="text-green-400">Correct: {sessionStats.correct}</span>
          <span className="text-red-400">Incorrect: {sessionStats.incorrect}</span>
        </div>

        {renderCoLearnerButton('', true)}
        {coLearnerEnabled && (
          <CoLearnerPanel
            isOpen={coLearnerOpen}
            onClose={() => setCoLearnerOpen(false)}
            bookId={bookId}
            task={task}
            questions={questions}
            progress={progress}
            styling={styling}
          />
        )}
      </div>
    );
  }

  // ==================== ONE-AT-A-TIME STUDY VIEW ====================
  if (mode === 'study' && (currentQuestion || branchQueue.length > 0)) {
    const activeQuestion = branchQueue.length > 0 ? branchQueue[0] : currentQuestion;
    const questionBox = getQuestionBox(activeQuestion.id, activeQuestion.isStem);
    const currentAttempts = sessionAttempts[activeQuestion.id] || 0;

    return (
      <div className="flex flex-col gap-4 max-w-4xl mx-auto pb-24 sm:pb-8">
        {branchAlert && (
          <div className="bg-indigo-600/90 text-white py-3 px-1 md:px-3 rounded-lg shadow-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-4">
            <CheckCircle2 className="h-5 w-5" />
            <p className="font-medium text-sm">{branchAlert}</p>
          </div>
        )}

        <div className={`flex gap-4 ${hintSidebarOpen ? 'lg:pr-0' : ''}`}>
          <div className="flex-1 space-y-4 transition-all">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  if (window.confirm('Exit study session? Progress is saved.')) {
                    setMode('menu');
                  }
                }}
                className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
                Exit Session
              </button>
              <span className="text-slate-400">
                {branchQueue.length > 0 ? 'Branch' : `${currentIndex + 1} / ${studyQueue.length}`}
              </span>
            </div>
            <div className="flex justify-end items-center gap-2">
              {showPresentationToggle && (
                <button
                  type="button"
                  onClick={() => setIsPresentationMode(prev => !prev)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700 transition"
                  aria-pressed={isPresentationMode}
                >
                  <span className="text-xs uppercase tracking-wide text-slate-400">
                    {isPresentationMode ? 'Presentation' : 'Stacked'}
                  </span>
                  <span
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isPresentationMode ? 'bg-indigo-500' : 'bg-slate-600'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isPresentationMode ? 'translate-x-4' : 'translate-x-1'}`}
                    />
                  </span>
                </button>
              )}
              {renderCoLearnerButton()}
            </div>

            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-300"
                style={{
                  width: `${branchQueue.length > 0 ? 100 : ((currentIndex + 1) / studyQueue.length) * 100}%`,
                  backgroundColor: styling?.primaryColor || '#6366f1'
                }}
              />
            </div>

            {!parentQuestionContext && (
              <div className="flex items-center gap-2 justify-center">
                <span className="text-sm text-slate-400">Current Box:</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map(box => (
                    <div
                      key={box}
                      className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold transition-all ${questionBox === box ? 'text-white scale-110' : questionBox > box ? 'bg-green-500/30 text-green-400' : 'bg-slate-700 text-slate-500'
                        }`}
                      style={questionBox === box ? { backgroundColor: styling?.primaryColor || '#6366f1' } : {}}
                    >
                      {box}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <QuestionCard
              key={`${activeQuestion.id}-${currentAttempts}-${parentQuestionContext?.id || 'main'}`}
              question={{ ...activeQuestion, text: activeQuestion.text || activeQuestion.questionText }}
              image={activeQuestion.image}
              onSubmit={handleAnswerSubmit}
              layout={isPresentationMode ? 'split' : 'stacked'}
              stemText={activeQuestion.isStem ? activeQuestion.stemText : undefined}
              stemImage={activeQuestion.isStem ? activeQuestion.stemImage : undefined}
              stemMedia={activeQuestion.isStem ? activeQuestion.stemMedia : undefined}
              parentQuestion={parentQuestionContext} // Inject parent context
              progress={activeQuestion.isStem ? {
                current: studyQueue.filter((q, i) => i <= currentIndex && q.stemId === activeQuestion.stemId).length,
                total: studyQueue.filter(q => q.stemId === activeQuestion.stemId).length
              } : null}
              sessionAttempts={currentAttempts}
              maxAttempts={MAX_SESSION_ATTEMPTS}
              onRequestHint={handleRequestHint}
              isRetrying={isRetrying}
              showAnswersAfterEachResponse={task?.showAnswersAfterEachResponse === true}
            />

            <div className="flex justify-center gap-6 text-sm">
              <span className="text-green-400">✓ {sessionStats.correct} correct</span>
              <span className="text-red-400">✗ {sessionStats.incorrect} incorrect</span>
            </div>
          </div>

          {hintSidebarOpen && hintQuestion && (
            <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-slate-900 border-l border-slate-700 shadow-2xl z-50 flex flex-col">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between bg-amber-500/10">
                <div className="flex items-center gap-2 text-amber-400">
                  <Lightbulb className="h-5 w-5" />
                  <span className="font-semibold">Hint</span>
                </div>
                <button onClick={closeHintSidebar} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
                  <X className="h-5 w-5 text-slate-400" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {hintQuestion.hint && (
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <p className="text-xs text-slate-500 uppercase font-medium mb-2">Text Hint</p>
                    <div className="text-slate-200">
                      <MathRenderer content={hintQuestion.hint.text} enableMarkdown={true} enableMath={true} />
                    </div>
                  </div>
                )}

                {hintQuestion.hintAudio && (
                  <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <p className="text-xs text-slate-500 uppercase font-medium mb-2 flex items-center gap-2">
                      <Volume2 className="h-4 w-4" />
                      Audio Hint
                    </p>
                    <audio controls src={hintQuestion.hintAudio} className="w-full" />
                  </div>
                )}

                {!hintQuestion.hint && !hintQuestion.hintAudio && (
                  <div className="text-center py-8 text-slate-500">
                    <Lightbulb className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hint available for this question.</p>
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-slate-700 bg-slate-800/50">
                <button
                  onClick={handleRetryWithHint}
                  className="w-full py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-2"
                  style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
                >
                  <RotateCcw className="h-5 w-5" />
                  Try Again
                </button>
                <p className="text-xs text-slate-500 text-center mt-2">
                  {MAX_SESSION_ATTEMPTS - (sessionAttempts[currentQuestion?.id] || 0) - 1} {MAX_SESSION_ATTEMPTS - (sessionAttempts[currentQuestion?.id] || 0) - 1 === 1 ? 'retry' : 'retries'} remaining
                </p>
              </div>
            </div>
          )}
        </div>

        {renderCoLearnerButton('', true)}
        {coLearnerEnabled && (
          <CoLearnerPanel
            isOpen={coLearnerOpen}
            onClose={() => setCoLearnerOpen(false)}
            bookId={bookId}
            task={task}
            questions={questions}
            progress={progress}
            styling={styling}
          />
        )}
      </div>
    );
  }

  // ==================== COMPLETE VIEW ====================
  if (mode === 'complete') {
    const totalAnswered = sessionStats.correct + sessionStats.incorrect;
    const accuracy = totalAnswered > 0 ? Math.round((sessionStats.correct / totalAnswered) * 100) : 0;

    return (
      <div className="text-center space-y-6 pb-24 sm:pb-8">
        <div className="py-8">
          <Trophy className="h-16 w-16 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            {displayMode === 'all-at-once' ? 'Test Complete!' : 'Session Complete!'}
          </h2>
          <p className="text-slate-400">Great work on your {displayMode === 'all-at-once' ? 'test' : 'study session'}</p>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{totalAnswered}</div>
              <div className="text-sm text-slate-400">Questions</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">{sessionStats.correct}</div>
              <div className="text-sm text-slate-400">Correct</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400">{accuracy}%</div>
              <div className="text-sm text-slate-400">Accuracy</div>
            </div>
          </div>
          {useSections && (task.sections || []).filter(s => s.type === 'note').length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-4 mt-4 pt-4 border-t border-slate-600">
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-400">
                  {(task.sections || []).filter(s => s.type === 'note').length}
                </div>
                <div className="text-sm text-slate-400">Notes Read</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-teal-400">
                  {progress?.noteReadings ? Object.keys(progress.noteReadings).length : 0}
                </div>
                <div className="text-sm text-slate-400">Total Readings</div>
              </div>
            </div>
          )}
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${accuracy}%` }} />
          </div>
          {task?.passingScore && (
            <div className="mt-4 text-center">
              {accuracy >= task.passingScore ? (
                <span className="text-green-400 font-medium">✓ Passed (Required: {task.passingScore}%)</span>
              ) : (
                <span className="text-red-400 font-medium">✗ Below passing score ({task.passingScore}%)</span>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-4 justify-center">
          <button
            onClick={() => {
              onComplete({
                correct: sessionStats.correct,
                incorrect: sessionStats.incorrect,
                questionsStudied: sessionStats.correct + sessionStats.incorrect,
                accuracy,
                answers: sessionAnswersRef.current
              });
            }}
            className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors"
          >
            Done
          </button>
          <button
            onClick={startStudy}
            className="px-6 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: styling?.primaryColor || '#6366f1' }}
          >
            <RotateCcw className="h-5 w-5" />
            {displayMode === 'all-at-once' ? 'Retake Test' : 'Study Again'}
          </button>
        </div>

        {renderCoLearnerButton('', true)}
        {coLearnerEnabled && (
          <CoLearnerPanel
            isOpen={coLearnerOpen}
            onClose={() => setCoLearnerOpen(false)}
            bookId={bookId}
            task={task}
            questions={questions}
            progress={progress}
            styling={styling}
          />
        )}
      </div>
    );
  }

  return null;
};

export default TaskTakerView;
